import axios from 'axios'

import type { Partner } from '@/types/types'

export type ApiError = {
  message: string
  status: number
  details?: any
}

export default function usePartner() {
  const { t } = useI18n()

  const fetchAllMarketplacePartners = async (): Promise<Partner[]> => {
    try {
      const { data } = await axios.get('partners', {
        params: {
          isVisibleInMarketplace: 1,
          finalRedirectUri: `/marketplace?partner={partner}`
        }
      })

      return data.data || []
    } catch (error: any) {
      console.error('Error loading all marketplace partners:', error)
      return []
    }
  }

  const fetchMarketplacePartners = async (): Promise<Partner[]> => {
    try {
      const { data } = await axios.get('/partners', {
        params: {
          isVisibleInMarketplace: 1,
          finalRedirectUri: `/marketplace?partner={partner}`
        }
      })

      const partnersWithAccounts = await Promise.all(
        data.data.map(async (partner: Partner) => {
          try {
            const accountsResponse = await axios.get(`/partners/${partner.id}/accounts`, {
              params: {
                finalRedirectUri: `/marketplace?partner={partner}`
              }
            })
            const activeAccounts = accountsResponse.data.data.filter((a: any) => a.isActive)
            return {
              ...partner,
              accountsNumber: activeAccounts.length
            }
          } catch (error) {
            console.error(`Error loading accounts for partner ${partner.id}:`, error)
            return {
              ...partner,
              accountsNumber: 0
            }
          }
        })
      )

      return partnersWithAccounts.filter(
        (partner: Partner & { accountsNumber?: number }) => (partner.accountsNumber || 0) > 0
      )
    } catch (error: any) {
      console.error('Error loading marketplace partners:', error)
      return []
    }
  }

  const fetchPartnerById = async (partnerId: number): Promise<Partner | null> => {
    try {
      const { data } = await axios.get(`/partners/${partnerId}`)
      return data.data
    } catch (error: any) {
      if (error.response?.status === 404) {
        return null
      }
      const apiError: ApiError = {
        message: t('pages.partner.errors.fetchPartner'),
        status: error.response?.status || 500,
        details: error.response?.data
      }
      throw apiError
    }
  }

  const fetchPartnerAccounts = async (partnerId: number): Promise<any[]> => {
    try {
      const { data } = await axios.get(`/partners/${partnerId}/accounts`)
      return data.data || []
    } catch (error: any) {
      const apiError: ApiError = {
        message: t('pages.partner.errors.fetchAccounts'),
        status: error.response?.status || 500,
        details: error.response?.data
      }
      throw apiError
    }
  }

  const getPartnerParameters = async (params: any = {}): Promise<any[]> => {
    try {
      const { data } = await axios.get('/partner-parameters', { params })
      return data.data || []
    } catch (error: any) {
      console.error('Error loading partner parameters:', error)
      return []
    }
  }

  return {
    fetchMarketplacePartners,
    fetchAllMarketplacePartners,
    fetchPartnerById,
    fetchPartnerAccounts,
    getPartnerParameters
  }
}
